import type { PublicTrustSnapshot } from '../types';
import { trustHeroActivityNote, trustHeroAssurance } from '../trustCopy';
import { sealNamesEuAiAct, trustSealState, trustSiteHostname, safeHttpUrl } from '../trustSeal';
import { TrustKeyTerms } from '../trustKeyTerms';
import { TrustLiveStatusPanel } from './TrustLiveStatusPanel';

/**
 * Centered hero: one vertical axis: status pill, tenant identity, assurance,
 * seal. No side-by-side split (a two-column hero left the right half of the
 * viewport empty). The seal is the single branded trust-mark and sits as the
 * hero's anchor, linking out to complyedge.io.
 */
export function TrustProfileHero({
  slug,
  snapshot,
}: {
  slug: string;
  snapshot: PublicTrustSnapshot;
}) {
  const state = trustSealState(snapshot);
  const title = snapshot.display_name ?? slug;
  const originUrl = safeHttpUrl(snapshot.website_url);
  const originHost = originUrl ? trustSiteHostname(originUrl) : null;
  const assurance = trustHeroAssurance(state, sealNamesEuAiAct(snapshot));
  const activityNote = trustHeroActivityNote(state, snapshot.checks_24h ?? 0);

  return (
    <section className="trust-hero relative overflow-hidden border-b border-white/10">
      <div className="trust-hero-glow pointer-events-none absolute inset-0" aria-hidden />
      <div className="trust-hero-grid pointer-events-none absolute inset-0 opacity-40" aria-hidden />

      <div className="relative mx-auto max-w-5xl px-5 sm:px-6 lg:px-8 pt-10 pb-10 text-center lg:pt-12 lg:pb-12">
        <h1 className="font-display text-4xl font-bold tracking-tight text-white md:text-5xl">
          {title}
        </h1>

        {originUrl && originHost ? (
          <p className="mt-4 font-mono text-sm text-gray-400">
            <a
              href={originUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="trust-origin-link inline-flex items-baseline gap-1"
            >
              <span className="trust-origin-link__text">{originHost}</span>
              <span aria-hidden className="trust-origin-link__icon">
                ↗
              </span>
            </a>
          </p>
        ) : null}

        <p className="mt-6 max-w-2xl mx-auto text-base leading-relaxed text-gray-200 md:text-lg">
          <TrustKeyTerms>{assurance}</TrustKeyTerms>
        </p>

        {activityNote ? (
          <p className="mt-3 font-mono text-sm text-gray-400">{activityNote}</p>
        ) : null}

        <div className="mt-8 text-left">
          <TrustLiveStatusPanel snapshot={snapshot} />
        </div>
      </div>
    </section>
  );
}
